import React, { useState, useMemo } from "react";
import {
  ChakraProvider,
  ColorModeScript,
  useColorMode,
  useColorModeValue,
  Box,
  Heading,
  Text,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Button,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tfoot,
  Progress,
  Stack,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Badge,
  IconButton,
  SimpleGrid,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";

function InnerApp() {
  const { colorMode, toggleColorMode } = useColorMode();
  const cardBg = useColorModeValue("white", "gray.800");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const subtleText = useColorModeValue("gray.600", "gray.300");

  // GLOBAL SETTINGS
  const [hoursPerDay, setHoursPerDay] = useState(7.5);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [vacationDays, setVacationDays] = useState(25);

  // New: explicit weeks/year & optional override for working days/year
  const [weeksPerYear, setWeeksPerYear] = useState(52);
  const [customDaysPerYear, setCustomDaysPerYear] = useState(0); // 0 = auto

  // PROJECTS
  const [projects, setProjects] = useState([]);

  const [newName, setNewName] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [inputType, setInputType] = useState("hours_day");
  const [inputValue, setInputValue] = useState(0);

  // Topic goal comparison
  const [goalTopic, setGoalTopic] = useState("");
  const [goalPercent, setGoalPercent] = useState(30);

  // CALCULATIONS
  const computedWorkingDays = weeksPerYear * daysPerWeek - vacationDays;
  const yearlyWorkingDays =
    customDaysPerYear && customDaysPerYear > 0
      ? customDaysPerYear
      : computedWorkingDays;

  const weeklyAvailable = hoursPerDay * daysPerWeek;
  const yearlyAvailable = yearlyWorkingDays * hoursPerDay;

  // NORMALIZATION LOGIC
  function normalize(project) {
    const v = Number(project.value) || 0;

    switch (project.type) {
      case "hours_day":
        return {
          hoursDay: v,
          hoursWeek: v * daysPerWeek,
          hoursYear: v * yearlyWorkingDays,
        };
      case "hours_week":
        return {
          hoursDay: daysPerWeek ? v / daysPerWeek : 0,
          hoursWeek: v,
          hoursYear: v * weeksPerYear,
        };
      case "hours_year":
        return {
          hoursDay: yearlyWorkingDays ? v / yearlyWorkingDays : 0,
          hoursWeek:
            yearlyWorkingDays && daysPerWeek
              ? (v / yearlyWorkingDays) * daysPerWeek
              : 0,
          hoursYear: v,
        };
      case "%": {
        const yearly = (v / 100) * yearlyAvailable;
        return {
          hoursDay: yearlyWorkingDays ? yearly / yearlyWorkingDays : 0,
          hoursWeek:
            yearlyWorkingDays && daysPerWeek
              ? (yearly / yearlyWorkingDays) * daysPerWeek
              : 0,
          hoursYear: yearly,
        };
      }
      default:
        return { hoursDay: 0, hoursWeek: 0, hoursYear: 0 };
    }
  }

  const normalizedProjects = projects.map((p) => ({
    ...p,
    ...normalize(p),
  }));

  const totalWeeklyLoad = normalizedProjects.reduce(
    (s, p) => s + p.hoursWeek,
    0
  );
  const totalYearlyLoad = normalizedProjects.reduce(
    (s, p) => s + p.hoursYear,
    0
  );
  const workloadPercent = yearlyAvailable
    ? (totalYearlyLoad / yearlyAvailable) * 100
    : 0;

  // Unique topics for dropdown
  const topicOptions = useMemo(() => {
    const set = new Set(
      projects
        .map((p) => p.topic?.trim())
        .filter((t) => t && t.length > 0)
    );
    return Array.from(set);
  }, [projects]);

  // Topic comparison
  const topicActualPercent = useMemo(() => {
    if (!goalTopic || !yearlyAvailable) return 0;
    const sumYearly = normalizedProjects
      .filter(
        (p) =>
          p.topic &&
          p.topic.trim().toLowerCase() === goalTopic.trim().toLowerCase()
      )
      .reduce((s, p) => s + p.hoursYear, 0);
    return (sumYearly / yearlyAvailable) * 100;
  }, [normalizedProjects, goalTopic, yearlyAvailable]);

  const topicDelta = topicActualPercent - goalPercent;

  // ADD PROJECT
  function addProject() {
    if (!newName) return;
    setProjects([
      ...projects,
      {
        id: Date.now(),
        name: newName,
        topic: newTopic.trim(),
        type: inputType,
        value: Number(inputValue) || 0,
      },
    ]);
    setNewName("");
    setNewTopic("");
    setInputValue(0);
  }

  function removeProject(id) {
    setProjects(projects.filter((p) => p.id !== id));
  }

  return (
    <Box bg={pageBg} minH="100vh" py={8}>
      <Box maxW="1200px" mx="auto" px={4}>
        {/* HEADER */}
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg">Workload Planner</Heading>
            <Text fontSize="sm" color={subtleText}>
              Plan by hours or percentages and see the true weight of each
              topic across your year.
            </Text>
          </Box>

          <IconButton
            aria-label="Toggle color mode"
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            size="lg"
          />
        </Flex>

        {/* SETTINGS */}
        <Card mb={8} bg={cardBg} shadow="md" borderRadius="lg">
          <CardHeader pb={3}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Global Working Time</Heading>
              <Badge colorScheme="blue" fontSize="0.8rem">
                Baseline for all calculations
              </Badge>
            </Flex>
          </CardHeader>

          <CardBody pt={0}>
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              mb={6}
              mt={4}
            >
              <FormControl>
                <FormLabel>Hours per day</FormLabel>
                <NumberInput
                  value={hoursPerDay}
                  min={0}
                  onChange={(_, vNum) => setHoursPerDay(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Working days per week</FormLabel>
                <NumberInput
                  value={daysPerWeek}
                  min={0}
                  onChange={(_, vNum) => setDaysPerWeek(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Vacation days (year)</FormLabel>
                <NumberInput
                  value={vacationDays}
                  min={0}
                  onChange={(_, vNum) => setVacationDays(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </Stack>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={6}
              mb={4}
            >
              <FormControl>
                <FormLabel>Weeks per year</FormLabel>
                <NumberInput
                  value={weeksPerYear}
                  min={1}
                  onChange={(_, vNum) => setWeeksPerYear(vNum || 1)}
                >
                  <NumberInputField />
                </NumberInput>
                <Text fontSize="xs" color={subtleText} mt={1}>
                  Used when converting hours/week to hours/year.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Working days per year (override)</FormLabel>
                <NumberInput
                  value={customDaysPerYear}
                  min={0}
                  onChange={(_, vNum) => setCustomDaysPerYear(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
                <Text fontSize="xs" color={subtleText} mt={1}>
                  0 = auto-calc from weeks, days/week & vacation. Currently:
                  {" "}{Math.max(Math.round(computedWorkingDays), 0)} days.
                </Text>
              </FormControl>
            </Stack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={2}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={3}
                textAlign="center"
              >
                <Text fontSize="xs" color={subtleText}>
                  Working days / year (used)
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {isFinite(yearlyWorkingDays) ? yearlyWorkingDays : 0}
                </Text>
              </Box>

              <Box
                borderWidth="1px"
                borderRadius="md"
                p={3}
                textAlign="center"
              >
                <Text fontSize="xs" color={subtleText}>
                  Available hours / year
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {isFinite(yearlyAvailable)
                    ? yearlyAvailable.toFixed(1)
                    : "0.0"}
                </Text>
              </Box>

              <Box
                borderWidth="1px"
                borderRadius="md"
                p={3}
                textAlign="center"
              >
                <Text fontSize="xs" color={subtleText}>
                  Available hours / week
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {isFinite(weeklyAvailable)
                    ? weeklyAvailable.toFixed(1)
                    : "0.0"}
                </Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* ADD PROJECT */}
        <Card mb={8} bg={cardBg} shadow="md" borderRadius="lg">
          <CardHeader pb={3}>
            <Heading size="md">Add Project / Work Package</Heading>
          </CardHeader>

          <CardBody pt={0}>
            <Stack
              spacing={4}
              direction={{ base: "column", md: "row" }}
              align={{ base: "stretch", md: "center" }}
              mt={4}
            >
              <FormControl>
                <FormLabel>Project name</FormLabel>
                <Input
                  placeholder="e.g. Feature X, Customer Y..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Topic / category</FormLabel>
                <Input
                  placeholder="e.g. Research, Customer work, Admin..."
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                />
              </FormControl>
            </Stack>

            <Stack
              spacing={4}
              direction={{ base: "column", md: "row" }}
              align={{ base: "stretch", md: "center" }}
              mt={4}
            >
              <FormControl>
                <FormLabel>Input type</FormLabel>
                <Select
                  value={inputType}
                  onChange={(e) => setInputType(e.target.value)}
                >
                  <option value="hours_day">Hours / day</option>
                  <option value="hours_week">Hours / week</option>
                  <option value="hours_year">Hours / year</option>
                  <option value="%">% of total working time</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Value</FormLabel>
                <NumberInput
                  value={inputValue}
                  min={0}
                  onChange={(_, vNum) => setInputValue(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>

              <Box pt={{ base: 2, md: 7 }}>
                <Button
                  colorScheme="blue"
                  w={{ base: "100%", md: "auto" }}
                  onClick={addProject}
                >
                  Add project
                </Button>
              </Box>
            </Stack>

            <Text fontSize="xs" color={subtleText} mt={2}>
              Example: “2 h/day” for recurring work, “5 h/week” for a smaller
              responsibility, “100 h/year” for a fixed effort, or “30%” for a
              strategic topic.
            </Text>
          </CardBody>
        </Card>

        {/* PROJECT TABLE */}
        <Heading size="md" mb={3}>
          Project Workload Breakdown
        </Heading>
        <Text fontSize="sm" color={subtleText} mb={3}>
          Every project is normalized to hours/day, week, year and percentage of
          your true available working time.
        </Text>

        <Box
          bg={cardBg}
          borderRadius="lg"
          shadow="md"
          overflowX="auto"
          mb={8}
        >
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Project</Th>
                <Th>Topic</Th>
                <Th>Status</Th>
                <Th>Input</Th>
                <Th isNumeric>Hours / day</Th>
                <Th isNumeric>Hours / week</Th>
                <Th isNumeric>Hours / year</Th>
                <Th isNumeric>% of total</Th>
                <Th></Th>
              </Tr>
            </Thead>

            <Tbody>
              {normalizedProjects.map((p) => {
                const pct = yearlyAvailable
                  ? (p.hoursYear / yearlyAvailable) * 100
                  : 0;
                const badgeColor =
                  pct > 100 ? "red" : pct > 80 ? "orange" : "green";
                const badgeLabel =
                  pct > 100 ? "Overloaded" : pct > 80 ? "High" : "OK";

                const inputLabel =
                  p.type === "hours_day"
                    ? "h/day"
                    : p.type === "hours_week"
                    ? "h/week"
                    : p.type === "hours_year"
                    ? "h/year"
                    : "%";

                return (
                  <Tr key={p.id}>
                    <Td>
                      <Text fontWeight="semibold">{p.name}</Text>
                    </Td>

                    <Td>
                      {p.topic ? (
                        <Badge variant="subtle" colorScheme="purple">
                          {p.topic}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color={subtleText}>
                          –
                        </Text>
                      )}
                    </Td>

                    <Td>
                      <Badge colorScheme={badgeColor}>{badgeLabel}</Badge>
                    </Td>

                    <Td>
                      {p.value} {inputLabel}
                    </Td>

                    <Td isNumeric>{p.hoursDay.toFixed(2)}</Td>
                    <Td isNumeric>{p.hoursWeek.toFixed(2)}</Td>
                    <Td isNumeric>{p.hoursYear.toFixed(1)}</Td>
                    <Td isNumeric>{pct.toFixed(1)}%</Td>

                    <Td>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeProject(p.id)}
                      >
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                );
              })}

              {normalizedProjects.length === 0 && (
                <Tr>
                  <Td colSpan={9}>
                    <Text fontSize="sm" color={subtleText} textAlign="center">
                      No projects yet. Add a project above to see the workload
                      breakdown.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>

            <Tfoot>
              <Tr>
                <Th>Total</Th>
                <Th></Th>
                <Th></Th>
                <Th></Th>
                <Th isNumeric>-</Th>
                <Th isNumeric>{totalWeeklyLoad.toFixed(2)}</Th>
                <Th isNumeric>{totalYearlyLoad.toFixed(1)}</Th>
                <Th isNumeric>
                  {isFinite(workloadPercent) ? workloadPercent.toFixed(1) : 0}%
                </Th>
                <Th></Th>
              </Tr>
            </Tfoot>
          </Table>
        </Box>

        {/* TOPIC GOAL COMPARISON */}
        <Heading size="md" mb={3}>
          Topic Goal Comparison
        </Heading>
        <Text fontSize="sm" color={subtleText} mb={3}>
          Pick a topic and a target percentage. See how your current plan for
          that topic compares to your goal.
        </Text>

        <Card mb={8} bg={cardBg} shadow="md" borderRadius="lg">
          <CardBody>
            <Stack
              spacing={4}
              direction={{ base: "column", md: "row" }}
              mb={4}
            >
              <FormControl>
                <FormLabel>Topic</FormLabel>
                <Select
                  placeholder="Select from existing topics or type below"
                  value={goalTopic}
                  onChange={(e) => setGoalTopic(e.target.value)}
                >
                  {topicOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
                <Input
                  mt={2}
                  placeholder="…or type topic name manually"
                  value={goalTopic}
                  onChange={(e) => setGoalTopic(e.target.value)}
                />
              </FormControl>

              <FormControl maxW={{ base: "100%", md: "200px" }}>
                <FormLabel>Goal % of total time</FormLabel>
                <NumberInput
                  value={goalPercent}
                  min={0}
                  max={200}
                  onChange={(_, vNum) => setGoalPercent(vNum || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </Stack>

            <Divider my={3} />

            {goalTopic ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={3}>
                  <Box>
                    <Text fontSize="xs" color={subtleText}>
                      Topic
                    </Text>
                    <Text fontSize="lg" fontWeight="bold">
                      {goalTopic}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color={subtleText}>
                      Planned %
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold">
                      {topicActualPercent.toFixed(1)}%
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color={subtleText}>
                      Difference vs goal
                    </Text>
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color={topicDelta > 0 ? "red.400" : "green.400"}
                    >
                      {topicDelta > 0 ? "+" : ""}
                      {topicDelta.toFixed(1)}%
                    </Text>
                  </Box>
                </SimpleGrid>

                <Text fontSize="sm" mb={1}>
                  Topic share of your year:
                </Text>
                <Progress
                  value={topicActualPercent}
                  height="20px"
                  borderRadius="md"
                  colorScheme={
                    topicActualPercent < goalPercent * 0.8
                      ? "blue"
                      : topicActualPercent <= goalPercent * 1.2
                      ? "green"
                      : "orange"
                  }
                />
              </>
            ) : (
              <Text fontSize="sm" color={subtleText}>
                Select or type a topic to see how much of your time is planned
                for it versus your goal.
              </Text>
            )}
          </CardBody>
        </Card>

        {/* OVERALL SUMMARY */}
        <Heading size="md" mb={3}>
          Overall Load vs Capacity
        </Heading>
        <Card bg={cardBg} shadow="md" borderRadius="lg">
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
              <Box>
                <Text fontSize="xs" color={subtleText}>
                  Planned hours / week
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {totalWeeklyLoad.toFixed(2)}{" "}
                  <Text as="span" fontSize="sm" color={subtleText}>
                    / {weeklyAvailable.toFixed(2)} h
                  </Text>
                </Text>
              </Box>

              <Box>
                <Text fontSize="xs" color={subtleText}>
                  Planned hours / year
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {totalYearlyLoad.toFixed(1)}{" "}
                  <Text as="span" fontSize="sm" color={subtleText}>
                    / {yearlyAvailable.toFixed(1)} h
                  </Text>
                </Text>
              </Box>

              <Box>
                <Text fontSize="xs" color={subtleText}>
                  Free capacity (year)
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {Math.max(yearlyAvailable - totalYearlyLoad, 0).toFixed(1)} h
                </Text>
              </Box>
            </SimpleGrid>

            <Text fontSize="sm" mb={1}>
              Total workload:{" "}
              <strong>
                {isFinite(workloadPercent)
                  ? workloadPercent.toFixed(1)
                  : 0}
                %
              </strong>
            </Text>

            <Progress
              value={workloadPercent}
              height="26px"
              borderRadius="md"
              mt={1}
              colorScheme={
                workloadPercent < 80
                  ? "green"
                  : workloadPercent < 100
                  ? "orange"
                  : "red"
              }
            />

            <Text fontSize="xs" color={subtleText} mt={2}>
              &lt; 80% = comfortable · 80–100% = high load · &gt; 100% =
              overload
            </Text>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <ChakraProvider>
      <ColorModeScript />
      <InnerApp />
    </ChakraProvider>
  );
}
